/**
 * Web Token Storage Service
 *
 * Secure token storage implementation for web platform using httpOnly cookies.
 * This provides maximum security by preventing JavaScript access to tokens,
 * protecting against XSS attacks while maintaining functionality.
 *
 * Security Features:
 * - httpOnly cookies prevent JavaScript access
 * - Secure flag for HTTPS-only transmission
 * - SameSite protection against CSRF
 * - Automatic expiration handling
 * - Cross-tab synchronization via cookie events
 * - Memory fallback for development/testing
 */

import { container } from 'tsyringe';

import { TOKENS } from '@personal-trainer-app/shared';
import {
  STORAGE_KEYS,
  getStorageKey,
  parseJwtExpiration,
  createTokenValidationResult,
} from '@personal-trainer-app/shared';
import {
  DEFAULT_TOKEN_STORAGE_OPTIONS,
  DEFAULT_TOKEN_TYPE,
} from '@personal-trainer-app/shared/src/types/auth/storage.types';
import { isTokenExpired } from '@personal-trainer-app/shared/src/utils/auth/session-utils';
import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import type {
  ITokenStorageService,
  TokenData,
  TokenValidationResult,
  TokenStorageOptions,
  TokenStorageInfo,
  TokenMetadata,
} from '@personal-trainer-app/shared/src/types/auth/storage.types';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface CookieOptions {
  expires?: dayjs.Dayjs;
  maxAge?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

interface WebTokenStorageOptions extends TokenStorageOptions {
  cookieDomain?: string;
  cookiePath?: string;
  useSecureCookies?: boolean;
  useHttpOnlyCookies?: boolean;
}

// =============================================================================
// MEMORY STORAGE FALLBACK
// =============================================================================

class MemoryStorage {
  private storage = new Map<string, string>();

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  getItem(key: string): string | null {
    return this.storage.get(key) || null;
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

// =============================================================================
// WEB TOKEN STORAGE SERVICE
// =============================================================================

export class WebTokenStorageService implements ITokenStorageService {
  private options: Required<WebTokenStorageOptions>;
  private memoryFallback: MemoryStorage;
  private isServerSide: boolean;

  constructor(options: WebTokenStorageOptions = {}) {
    this.options = {
      ...DEFAULT_TOKEN_STORAGE_OPTIONS,
      cookieDomain: options.cookieDomain || window.location.hostname,
      cookiePath: options.cookiePath || '/',
      useSecureCookies:
        options.useSecureCookies ?? location.protocol === 'https:',
      useHttpOnlyCookies: options.useHttpOnlyCookies ?? true,
      ...options,
    };

    this.memoryFallback = new MemoryStorage();
    this.isServerSide = typeof window === 'undefined';

    // Clean up expired tokens on initialization
    this.cleanupExpiredTokens();

    // Set up cookie change listener for cross-tab synchronization
    if (!this.isServerSide) {
      this.setupCookieListener();
    }
  }

  // ===========================================================================
  // PUBLIC TOKEN MANAGEMENT METHODS
  // ===========================================================================

  async setTokens(
    accessToken: string,
    refreshToken?: string,
    customExpiration?: number
  ): Promise<boolean> {
    try {
      // Parse expiration from JWT or use custom expiration
      let expiresAt = customExpiration;
      if (!expiresAt) {
        const jwtExpiration = parseJwtExpiration(accessToken);
        expiresAt = jwtExpiration || dayjs().add(1, 'hour').valueOf(); // Default 1 hour
      }

      // refreshToken?: string;
      const tokenData: TokenData = {
        accessToken,
        refreshToken,
        expiresAt,
        issuedAt: dayjs().valueOf(),
        tokenType: DEFAULT_TOKEN_TYPE,
      };

      // Store tokens using appropriate method
      if (this.options.useHttpOnlyCookies && !this.isServerSide) {
        return await this.setTokensInCookies(tokenData);
      } else {
        return await this.setTokensInMemory(tokenData);
      }
    } catch (error) {
      console.error('Failed to store tokens:', error);
      return false;
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const tokenData = await this.getTokenData();

      if (!tokenData) {
        return null;
      }

      // Check if token is expired
      if (isTokenExpired(tokenData.expiresAt)) {
        await this.clearTokens();
        return null;
      }

      return tokenData.accessToken;
    } catch (error) {
      console.error('Failed to retrieve access token:', error);
      return null;
    }
  }

  async getRefreshToken(): Promise<string | null> {
    try {
      const tokenData = await this.getTokenData();
      return tokenData?.refreshToken || null;
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error);
      return null;
    }
  }

  async getTokenData(): Promise<TokenData | null> {
    try {
      if (this.options.useHttpOnlyCookies && !this.isServerSide) {
        return await this.getTokenDataFromCookies();
      } else {
        return await this.getTokenDataFromMemory();
      }
    } catch (error) {
      console.error('Failed to parse token data:', error);
      return null;
    }
  }

  async validateToken(): Promise<TokenValidationResult> {
    const tokenData = await this.getTokenData();
    return createTokenValidationResult(
      tokenData,
      this.options.refreshThreshold
    );
  }

  async clearTokens(): Promise<boolean> {
    try {
      if (this.options.useHttpOnlyCookies && !this.isServerSide) {
        return await this.clearTokensFromCookies();
      } else {
        return await this.clearTokensFromMemory();
      }
    } catch (error) {
      console.error('Failed to clear tokens:', error);
      return false;
    }
  }

  async hasTokens(): Promise<boolean> {
    const tokenData = await this.getTokenData();
    return !!tokenData && !isTokenExpired(tokenData.expiresAt);
  }

  async isTokenExpired(): Promise<boolean> {
    const tokenData = await this.getTokenData();
    return !tokenData || isTokenExpired(tokenData.expiresAt);
  }

  async getTimeUntilExpiration(): Promise<number> {
    const validation = await this.validateToken();
    return validation.expiresIn;
  }

  async getExpirationTime(): Promise<dayjs.Dayjs | null> {
    const tokenData = await this.getTokenData();
    return tokenData ? dayjs(tokenData.expiresAt) : null;
  }

  async getStorageInfo(): Promise<TokenStorageInfo> {
    const tokenData = await this.getTokenData();
    const validation = await this.validateToken();

    return {
      hasTokens: !!tokenData,
      tokenType: tokenData?.tokenType || null,
      expiresAt: tokenData ? dayjs(tokenData.expiresAt).toISOString() : null,
      issuedAt: tokenData ? dayjs(tokenData.issuedAt).toISOString() : null,
      isExpired: validation.isExpired,
      expiresIn: validation.expiresIn,
      storageType: this.options.useHttpOnlyCookies ? 'cookies' : 'memory',
    };
  }

  async exportTokenMetadata(): Promise<TokenMetadata> {
    const tokenData = await this.getTokenData();

    return {
      hasTokens: !!tokenData,
      expiresAt: tokenData?.expiresAt || null,
      issuedAt: tokenData?.issuedAt || null,
      tokenType: tokenData?.tokenType || null,
    };
  }

  async clearAllStorage(): Promise<boolean> {
    const result = await this.clearTokens();
    this.memoryFallback.clear();
    return result;
  }

  // ===========================================================================
  // PRIVATE COOKIE METHODS
  // ===========================================================================

  private async setTokensInCookies(tokenData: TokenData): Promise<boolean> {
    try {
      const serializedData = JSON.stringify(tokenData);
      const expiresAt = dayjs(tokenData.expiresAt);

      // Set access token cookie
      const accessTokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );

      this.setCookie(accessTokenKey, serializedData, {
        expires: expiresAt,
        httpOnly: true,
        secure: this.options.useSecureCookies,
        sameSite: 'strict',
        path: this.options.cookiePath,
        domain: this.options.cookieDomain,
      });

      // Set refresh token cookie if provided
      if (tokenData.refreshToken) {
        const refreshTokenKey = getStorageKey(
          STORAGE_KEYS.REFRESH_TOKEN,
          this.options.storagePrefix
        );

        this.setCookie(refreshTokenKey, tokenData.refreshToken, {
          expires: expiresAt,
          httpOnly: true,
          secure: this.options.useSecureCookies,
          sameSite: 'strict',
          path: this.options.cookiePath,
          domain: this.options.cookieDomain,
        });
      }

      return true;
    } catch (error) {
      console.error('Failed to set tokens in cookies:', error);
      return false;
    }
  }

  private async getTokenDataFromCookies(): Promise<TokenData | null> {
    try {
      const accessTokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );

      const cookieValue = this.getCookie(accessTokenKey);
      if (!cookieValue) {
        return null;
      }

      return JSON.parse(cookieValue) as TokenData;
    } catch (error) {
      console.error('Failed to get token data from cookies:', error);
      return null;
    }
  }

  private async clearTokensFromCookies(): Promise<boolean> {
    try {
      const accessTokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );
      const refreshTokenKey = getStorageKey(
        STORAGE_KEYS.REFRESH_TOKEN,
        this.options.storagePrefix
      );

      // Clear cookies by setting them to expire in the past
      this.setCookie(accessTokenKey, '', {
        expires: dayjs.unix(0),
        path: this.options.cookiePath,
        domain: this.options.cookieDomain,
      });

      this.setCookie(refreshTokenKey, '', {
        expires: dayjs.unix(0),
        path: this.options.cookiePath,
        domain: this.options.cookieDomain,
      });

      return true;
    } catch (error) {
      console.error('Failed to clear tokens from cookies:', error);
      return false;
    }
  }

  // ===========================================================================
  // PRIVATE MEMORY METHODS
  // ===========================================================================

  private async setTokensInMemory(tokenData: TokenData): Promise<boolean> {
    try {
      const serializedData = JSON.stringify(tokenData);
      const accessTokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );

      this.memoryFallback.setItem(accessTokenKey, serializedData);

      if (tokenData.refreshToken) {
        const refreshTokenKey = getStorageKey(
          STORAGE_KEYS.REFRESH_TOKEN,
          this.options.storagePrefix
        );
        this.memoryFallback.setItem(refreshTokenKey, tokenData.refreshToken);
      }

      return true;
    } catch (error) {
      console.error('Failed to set tokens in memory:', error);
      return false;
    }
  }

  private async getTokenDataFromMemory(): Promise<TokenData | null> {
    try {
      const accessTokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );

      const storedData = this.memoryFallback.getItem(accessTokenKey);
      if (!storedData) {
        return null;
      }

      return JSON.parse(storedData) as TokenData;
    } catch (error) {
      console.error('Failed to get token data from memory:', error);
      return null;
    }
  }

  private async clearTokensFromMemory(): Promise<boolean> {
    try {
      const accessTokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );
      const refreshTokenKey = getStorageKey(
        STORAGE_KEYS.REFRESH_TOKEN,
        this.options.storagePrefix
      );

      this.memoryFallback.removeItem(accessTokenKey);
      this.memoryFallback.removeItem(refreshTokenKey);

      return true;
    } catch (error) {
      console.error('Failed to clear tokens from memory:', error);
      return false;
    }
  }

  // ===========================================================================
  // PRIVATE UTILITY METHODS
  // ===========================================================================

  private setCookie(
    name: string,
    value: string,
    options: CookieOptions = {}
  ): void {
    if (this.isServerSide) return;

    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (options.expires) {
      cookieString += `; expires=${options.expires.toDate().toUTCString()}`;
    }

    if (options.maxAge) {
      cookieString += `; max-age=${options.maxAge}`;
    }

    if (options.path) {
      cookieString += `; path=${options.path}`;
    }

    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }

    if (options.secure) {
      cookieString += '; secure';
    }

    if (options.httpOnly) {
      cookieString += '; httpOnly';
    }

    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }

    document.cookie = cookieString;
  }

  private getCookie(name: string): string | null {
    if (this.isServerSide) return null;

    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name && cookieValue) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  private async cleanupExpiredTokens(): Promise<void> {
    try {
      const tokenData = await this.getTokenData();
      if (tokenData && isTokenExpired(tokenData.expiresAt)) {
        await this.clearTokens();
      }
    } catch (error) {
      console.error('Failed to cleanup expired tokens:', error);
    }
  }

  private setupCookieListener(): void {
    if (this.isServerSide) return;

    // Listen for storage events (cookies don't trigger storage events, but we can listen for custom events)
    window.addEventListener('storage', (event) => {
      const tokenKey = getStorageKey(
        STORAGE_KEYS.AUTH_TOKEN,
        this.options.storagePrefix
      );

      if (event.key === tokenKey && !event.newValue) {
        // Dispatch custom event for application to handle
        window.dispatchEvent(
          new CustomEvent('tokenCleared', {
            detail: { reason: 'external_change' },
          })
        );
      }
    });
  }
}

// self-register on import
if (!container.isRegistered(TOKENS.TOKEN_STORAGE)) {
  container.registerSingleton<ITokenStorageService>(
    TOKENS.TOKEN_STORAGE,
    WebTokenStorageService
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

// Create default instance
export const webTokenStorage = new WebTokenStorageService();

// Factory function for custom options
export const createWebTokenStorage = (
  options?: WebTokenStorageOptions
): WebTokenStorageService => {
  return new WebTokenStorageService(options);
};
